/**
 * Result-card action router extracted from `ChatBox.onCardAction`.
 *
 * An inline result card (matched email, note, contact, place, event, or an
 * email draft) was clicked. This pure function over an explicit deps object
 * reproduces the original inline behavior byte-for-byte:
 *
 *  - email/note/place/events `open` → route through `applyUiActions` with the
 *    same `module.focus` payloads (incl. the place/events catalog fallbacks).
 *  - contact `open` → open the ContactCardPopover (no module focus).
 *  - report `open` (#3793) → `pages.open` into the Pages chrome (module.focus
 *    fallback when no Pages container exists).
 *  - email_draft `compose` → reply-draft (thread present) or CRM compose.
 *  - email_draft `send` → send/reply and return a success boolean promise.
 *
 * The four network calls are routed through `@/lib/api/endpoints/email`, which
 * preserves the exact URL/method/header/body shapes of the original inline
 * fetches. All toast UX stays here (caller-owned), matching the original.
 */
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { ResultCardKind, PlaceCardData } from '@/features/chat/components/result-cards/types';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import {
  fetchEmailThread,
  draftEmailReply,
  crmDraftEmail,
  sendEmail,
} from '@/lib/api/endpoints/email';

export interface CardAction {
  kind: ResultCardKind;
  intent: 'open' | 'send' | 'compose';
  data: any;
}

export interface CardActionDeps {
  applyUiActions: (actions: ChatUIAction[]) => void;
  activeConversationId: string | undefined;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  token: string | null | undefined;
  setObjectCardPopover: (value: { objectType: string; objectId: string } | null) => void;
  setNoteModal: (value: { noteId: string } | null) => void;
  setDocModal: (value: { docId: string } | null) => void;
  workspaceModuleCatalogRef: { current: WorkspaceModule[] };
}

export type CardActionResult =
  | void
  | undefined
  | boolean
  | { body: string }
  | { subject: string; body: string }
  | Promise<undefined | boolean | { body: string } | { subject: string; body: string }>;

export function runCardAction(a: CardAction, deps: CardActionDeps): CardActionResult {
  const {
    applyUiActions,
    activeConversationId,
    showToast,
    token,
    setObjectCardPopover,
    setNoteModal,
    setDocModal,
    workspaceModuleCatalogRef,
  } = deps;

  if (a.kind === 'email' && a.intent === 'open') {
    applyUiActions([
      {
        type: 'module.focus',
        moduleType: 'email',
        objectId: a.data.threadId,
        conversationId: activeConversationId,
      } as ChatUIAction,
    ]);
    return;
  }

  if (a.kind === 'note' && a.intent === 'open') {
    // Pull the note up as PLAIN TEXT in a modal. Do NOT focus the Drive module
    // (its rich-text doc editor) — a note is not a doc. The Notes module is
    // reachable from the modal's "Open in Notes" for full editing.
    setNoteModal({ noteId: a.data.noteId });
    return;
  }

  if (a.kind === 'doc' && a.intent === 'open') {
    setDocModal({ docId: a.data.docId as string });
    return;
  }

  if (a.kind === 'contact' && a.intent === 'open') {
    // Opens the existing ContactCardPopover (no module focus needed).
    setObjectCardPopover({ objectType: 'contact', objectId: a.data.id });
    return;
  }

  if (a.kind === 'report' && a.intent === 'open') {
    // "Open report" CTA (#3793). Reuse the existing `pages.open` ui-action
    // (the xavior-letter affordance, apply-ui-actions.ts) so the user lands
    // inside the Pages chrome with the generated report selected — back arrow
    // + the rest of the Pages list intact. When no Pages container exists in
    // the catalog, fall back to focusing the report module directly (the same
    // legacy fallback applyReportDirective uses).
    const { moduleId } = a.data as { moduleId: string };
    const pagesModule = workspaceModuleCatalogRef.current.find(
      (m) => m.type === 'pages' && !m.archivedAt,
    );
    if (pagesModule?.id) {
      applyUiActions([
        {
          type: 'pages.open',
          parentPagesId: pagesModule.id,
          childModuleId: moduleId,
        } as ChatUIAction,
      ]);
    } else {
      applyUiActions([
        {
          type: 'module.focus',
          moduleId,
          conversationId: activeConversationId,
        } as ChatUIAction,
      ]);
    }
    return;
  }

  if (a.kind === 'place' && a.intent === 'open') {
    // Open the place in the REAL Places module (which renders the stored
    // object's photos correctly) rather than the bespoke popover, whose
    // on-demand photo enrichment didn't load. Mirrors the events→calendar
    // path: focus the places module and select the place by id; the module
    // (PlaceListModule's object-selection listener) opens its detail view.
    const placeData = a.data as unknown as PlaceCardData;
    const catalog = workspaceModuleCatalogRef.current;
    const moduleType = catalog.some((m) => m.type === 'place_list') ? 'place_list'
      : catalog.some((m) => m.type === 'places') ? 'places'
      : 'place_list';
    applyUiActions([
      {
        type: 'module.focus',
        moduleType,
        objectType: 'place',
        objectId: placeData.placeId,
        conversationId: activeConversationId,
      } as ChatUIAction,
    ]);
    return;
  }

  if (a.kind === 'events' && a.intent === 'open') {
    // Focus the calendar module. With an eventId (a single row) we open that
    // event; without one (the "Open Calendar" footer) we just surface the
    // module so the user can browse the range.
    const hasEventList = workspaceModuleCatalogRef.current.some((m) => m.type === 'event_list');
    applyUiActions([
      {
        type: 'module.focus',
        moduleType: hasEventList ? 'event_list' : 'calendar',
        objectType: a.data.eventId ? 'event' : undefined,
        objectId: a.data.eventId,
        conversationId: activeConversationId,
      } as ChatUIAction,
    ]);
    return;
  }

  if (a.kind === 'email_draft' && a.intent === 'compose') {
    // REPLY mode (inbox chip): draft a contextual reply in Xavior's voice via
    // the thread context + /draft-reply. No contactId needed.
    const replyCtx = a.data as { threadId?: string; subject?: string; style?: 'professional' | 'casual'; accountId?: string };
    if (replyCtx.threadId) {
      const threadId = replyCtx.threadId;
      return (async () => {
        try {
          // 1) Pull the thread for full message context.
          const tRes = await fetchEmailThread(token, { threadId, accountId: replyCtx.accountId });
          const tJson = tRes.ok ? await tRes.json().catch(() => ({})) : {};
          const messages = Array.isArray(tJson?.messages)
            ? tJson.messages.map((m: any) => ({ from: m.from, body: m.body || m.snippet || '', date: m.date }))
            : [];
          // 2) Draft the reply (SSE: data:{delta}/{done,draft}).
          const rRes = await draftEmailReply(token, {
            threadId,
            subject: replyCtx.subject,
            messages,
            tone: replyCtx.style,
            accountId: replyCtx.accountId,
          });
          if (!rRes.ok) { showToast('Could not draft a reply — try again', 'error'); return undefined; }
          const raw = await rRes.text();
          let draft = '';
          for (const line of raw.split('\n')) {
            const s = line.trim();
            if (!s.startsWith('data:')) continue;
            try {
              const p = JSON.parse(s.slice(5).trim());
              if (typeof p.draft === 'string' && p.draft) draft = p.draft;
              else if (typeof p.delta === 'string') draft += p.delta;
            } catch { /* ignore non-JSON SSE lines */ }
          }
          if (!draft.trim()) { showToast('Could not draft a reply — try again', 'error'); return undefined; }
          return { body: draft };
        } catch {
          showToast('Could not draft a reply — try again', 'error');
          return undefined;
        }
      })();
    }
    // Compose-from-rough-text: rewrite the card's current body into a
    // polished email in the chosen voice. Direct fetch (mirrors send) to the
    // CRM draft endpoint; returns {subject, body} for the card to fill.
    const { style, rewriteFrom, avoidBody, contactId, actionType } = a.data as {
      style: 'casual' | 'professional'; rewriteFrom?: string; avoidBody?: string; contactId?: string; actionType?: string;
    };
    if (!contactId) {
      showToast('No linked contact — type the email manually.', 'error');
      return undefined;
    }
    return (async () => {
      try {
        const res = await crmDraftEmail(token, {
          contactId,
          actionType: actionType || 'casual',
          composeStyle: style,
          rewriteFrom,
          avoidBody,
        });
        if (!res.ok) {
          // Surface the real reason (e.g. the 502 LLM_UNAVAILABLE message the
          // draft endpoint now returns) instead of a blanket toast — mirrors
          // the Send path below, which reads `message` off the error body.
          let msg = '';
          try {
            const parsed = await res.json();
            msg = typeof parsed?.message === 'string' ? parsed.message : '';
          } catch {
            // ignore parse errors — fall back to the generic message
          }
          showToast(msg || 'Could not compose — try again', 'error');
          return undefined;
        }
        const json = await res.json();
        const variant = json?.variants?.[0];
        if (!variant) {
          showToast('Could not compose — try again', 'error');
          return undefined;
        }
        return { subject: variant.subject ?? '', body: variant.body ?? '' };
      } catch {
        showToast('Could not compose — try again', 'error');
        return undefined;
      }
    })();
  }

  if (a.kind === 'email_draft' && a.intent === 'send') {
    // threadId/messageId present → in-thread reply (sets In-Reply-To/References).
    const { to, subject, body, threadId, messageId, accountId } = a.data as {
      to: string; subject: string; body: string; threadId?: string; messageId?: string; accountId?: string;
    };
    // Return the in-flight promise resolving to a success boolean so
    // EmailDraftCard can lock its Send button on success (preventing a
    // double-send of a real email) and re-enable it for a retry on failure.
    return (async () => {
      try {
        const res = await sendEmail(token, { to, subject, body, threadId, messageId, accountId });
        if (res.ok) {
          showToast(threadId ? 'Reply sent' : 'Email sent', 'success');
          return true;
        }
        let msg = '';
        try {
          const parsed = await res.json();
          msg = typeof parsed?.message === 'string' ? parsed.message : '';
        } catch {
          // ignore parse errors — fall back to the generic message
        }
        // Keep the card editable: EmailDraftCard returns to idle on a false
        // result so the draft stays in place for a retry.
        showToast(msg || 'Could not send — connect an email account', 'error');
        return false;
      } catch {
        showToast('Could not send — connect an email account', 'error');
        return false;
      }
    })();
  }
}
